/** `/conversations`: the phone's Conversations tab, or on a wider screen the place last opened. */
import { useEffect, useMemo } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { FirstRoomEmpty } from "../components/FirstRoomEmpty";
import { Face } from "../components/ui/Face";
import { Icon } from "../components/ui/Icon";
import { Poster } from "./parts";
import { allRoomsPath, announcementsPath, circlePath, directPath, everyonePath, filesPath, placesPath, roomHome, talkPath } from "./places";
import { RoomLine } from "./PhoneRooms";
import { firstRoom, kindLine, railSections } from "./rooms";
import { newCount, plainCount, Row } from "./phone";
import { rememberedHome, useTalkContext } from "./TalkLayout";
import { listTime } from "./when";

export function TalkHome() {
  const { rail, phase, lay } = useTalkContext();
  const [params] = useSearchParams();
  if (lay === "phone") return <PhoneList />;
  if (!rail) return phase === "error" ? <p className="empty-note">We couldn't load your conversations. Try again in a moment.</p> : null;
  const first = firstRoom(rail.rooms);
  const direct = params.get("tab") === "direct" && rail.direct[0] ? directPath(rail.direct[0].contactId) : null;
  const to = direct ?? rememberedHome() ?? (first ? roomHome(first) : rail.direct[0] ? directPath(rail.direct[0].contactId) : null);
  if (to) return <Navigate to={to} replace />;
  return (
    <main className="main">
      <FirstRoomEmpty place="conversations" />
    </main>
  );
}

function PhoneList() {
  const { rail, phase, startCircle, reloadRail } = useTalkContext();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "direct" ? "direct" : "rooms";
  const sections = useMemo(() => railSections(rail?.rooms ?? []), [rail]);
  useEffect(() => reloadRail(), [reloadRail]);

  return (
    <div className="hp">
      <div className="hp-scroll">
        <div className="hp-page">
          <h1 className="hp-title">Conversations</h1>
          <div className="rtabs hp-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === "rooms"} className={tab === "rooms" ? "on" : ""} onClick={() => setParams({}, { replace: true })}>
              Rooms {rail?.roomsNew ? <i>{rail.roomsNew}</i> : null}
            </button>
            <button type="button" role="tab" aria-selected={tab === "direct"} className={tab === "direct" ? "on" : ""} onClick={() => setParams({ tab: "direct" }, { replace: true })}>
              Direct {rail?.directUnread ? <i>{rail.directUnread}</i> : null}
            </button>
          </div>
          <button type="button" className="hp-q" onClick={() => navigate(`${talkPath}/search`)}>
            <Icon name="search" />
            Search messages, files, people
          </button>
          {phase === "error" && !rail ? <p className="empty-note">We couldn't load your conversations. Try again in a moment.</p> : null}
          {rail && tab === "rooms" ? (
            rail.rooms.length ? (
              <>
                {sections.kept.length ? (
                  <>
                    <p className="hp-sub big">
                      <Icon name="pin" /> Kept at the top
                    </p>
                    {sections.kept.map((r) => (
                      <section key={r.webinarId} className="hp-card hp-roomcard">
                        <button type="button" className="hp-card-h tap" onClick={() => navigate(placesPath(r.webinarId))}>
                          <Poster url={r.posterUrl} />
                          <span>
                            <b>{r.title}</b>
                            <small>{kindLine(r)}</small>
                          </span>
                          <Icon name="chev" />
                        </button>
                        <div className="hp-tree">
                          {r.everyoneChat ? <Row icon="users" label="Everyone" end={r.everyoneNew ? newCount(r.everyoneNew) : undefined} onClick={() => navigate(everyonePath(r.webinarId))} /> : null}
                          <Row icon="bell" label="Announcements" onClick={() => navigate(announcementsPath(r.webinarId))} />
                          <Row icon="fold" label="Files" end={plainCount(r.filesCount)} onClick={() => navigate(filesPath(r.webinarId))} />
                          <p className="hp-sub">Circles</p>
                          {r.circles.map((c) => (
                            <Row key={c.circleId} icon="target" label={c.name} end={c.live ? <span className="ch-live">Live</span> : c.newCount ? newCount(c.newCount) : plainCount(c.members)} onClick={() => navigate(circlePath(c.circleId))} />
                          ))}
                          <Row icon="plus" label="Start a circle" accent onClick={() => startCircle(r.webinarId)} />
                        </div>
                      </section>
                    ))}
                  </>
                ) : null}
                {sections.active.length ? (
                  <>
                    <p className="hp-sub big">Active this week</p>
                    <section className="hp-card">
                      {sections.active.map((r) => (
                        <RoomLine key={r.webinarId} room={r} />
                      ))}
                    </section>
                  </>
                ) : null}
                <section className="hp-card">
                  <button type="button" className="hp-row" onClick={() => navigate(allRoomsPath)}>
                    <Icon name="grid" />
                    <span className="hp-row-n">All rooms</span>
                    {plainCount(rail.rooms.length)}
                    <Icon name="chev" />
                  </button>
                </section>
              </>
            ) : (
              <FirstRoomEmpty place="conversations" />
            )
          ) : null}
          {rail && tab === "direct" ? (
            <section className="hp-card">
              {rail.direct.map((r) => (
                <button key={r.contactId} type="button" className="hp-person" onClick={() => navigate(directPath(r.contactId))}>
                  <Face name={r.name} photo={r.photoUrl} size={44} />
                  <span className="hp-person-t">
                    <b>
                      {r.name}
                      <small>{listTime(r.lastAt)}</small>
                    </b>
                    <span>{r.last}</span>
                  </span>
                  {r.unread ? <i className="pr-dot" /> : null}
                </button>
              ))}
              {rail.direct.length === 0 ? <p className="empty-note">Nobody here yet.</p> : null}
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}
